import { Bot, Globe, MessageSquare, type LucideIcon } from 'lucide-react';

/**
 * The SELL channel — the surface an order was AUTHORED on (D11 / D16). Mirror of the backend's
 * `SellChannel`; uppercase on the wire.
 *
 * This module is the single home for the vocabulary. It used to be declared once per feature
 * (`order-board-client`, `discounts-client`) with the display maps copied a third and fourth time
 * into the board's two components, so adding a channel meant a hunt rather than an edit.
 *
 * NOT to be confused with the MESSAGING channel in `@/lib/channel.ts` (`whatsapp | instagram`) —
 * that one says which provider a conversation rides on, not where a sale was made. The two are
 * separate dimensions that happen to share the word "channel" and the label "WhatsApp"; the inbox's
 * chips read from `conversations:list.provider.*`, these from a namespace's `channel(s)` map.
 */
export type SellChannel = 'WHATSAPP' | 'WEB' | 'EXTERNAL_AGENT';

/** Every sell channel, in the order every surface lists them. */
export const SELL_CHANNELS: readonly SellChannel[] = ['WHATSAPP', 'WEB', 'EXTERNAL_AGENT'];

/**
 * Whether this BUILD ships the external-agent (MCP) channel — the "coming soon" gate.
 *
 * The connector exists in menteo-agent and is complete, but it has never been exercised against a
 * real MCP host, so munchies-api reports the channel as off for every tenant and refuses to turn it
 * on. Here it decides what an operator is SHOWN: the `/settings/external` section does not exist,
 * and no picker offers a channel that cannot produce an order.
 *
 * A hardcoded constant and not an env var: "not shipped yet" is a property of the build. Shipping
 * the channel is this literal plus the matching constants in munchies-api, menteo-agent,
 * menteo-loyalty-app and menteo-dashboard.
 */
export const EXTERNAL_AGENT_CHANNEL_AVAILABLE = false;

/**
 * The channels an operator may actually CHOOSE — the coupon restriction list, the board filter.
 *
 * Kept apart from {@link SELL_CHANNELS} because the two answer different questions. `SELL_CHANNELS`
 * is the WIRE vocabulary: menteo-agent stamps `EXTERNAL_AGENT` on orders and conversations, so
 * every label and tint below must keep resolving it or a row would render with no identity. This is
 * the selectable set — a filter that can only ever return nothing, and a checkbox that restricts
 * nothing, are worse than absent.
 */
export const AVAILABLE_SELL_CHANNELS: readonly SellChannel[] = SELL_CHANNELS.filter(
  (channel) => channel !== 'EXTERNAL_AGENT' || EXTERNAL_AGENT_CHANNEL_AVAILABLE,
);

export function isSellChannel(value: unknown): value is SellChannel {
  return typeof value === 'string' && (SELL_CHANNELS as readonly string[]).includes(value);
}

/**
 * The i18n key a channel's LABEL lives under, inside whichever namespace map holds it
 * (`orders:board.channel.*`, `coupons:channels.*`). camelCase is the canonical shape — the
 * uppercase wire values are kept as aliases in the locale files so older keys still resolve.
 *
 * `conversations:events.channel.*` deliberately does NOT follow this: those strings are sentence
 * fragments ("desde **la web**"), not labels, and must not be reused as chip text.
 */
const CHANNEL_LABEL_KEYS: Record<SellChannel, string> = {
  WHATSAPP: 'whatsapp',
  WEB: 'web',
  EXTERNAL_AGENT: 'externalAgent',
};

export function channelLabelKey(channel: SellChannel): string {
  // The `??` is for values off the wire, not for the union: `restrictedChannels` deliberately keeps
  // a channel this client does not know yet, and a raw wire value degrades better than `undefined`.
  return CHANNEL_LABEL_KEYS[channel] ?? channel;
}

/**
 * The chip tint for a channel — border + background + text, with an explicit `dark:` pair on each,
 * because a tint that only works in one mode reads as broken. Pair it with the `border` utility.
 *
 * One triple per channel, so the same order is not emerald on the board and sky on the coupon list.
 */
const CHANNEL_TONES: Record<SellChannel, string> = {
  WHATSAPP:
    'border-emerald-200 dark:border-emerald-800/60 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300',
  WEB: 'border-sky-200 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300',
  EXTERNAL_AGENT:
    'border-violet-200 dark:border-violet-800/60 bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300',
};

export function channelTone(channel: SellChannel): string {
  // Same reasoning as `channelLabelKey`: an unknown channel gets neutral chrome rather than none.
  return CHANNEL_TONES[channel] ?? 'border-border bg-muted text-muted-foreground';
}

/**
 * The glyph that stands for a channel, so a chip reads at a glance before its label is read at all.
 * Lives next to the labels and the tints for the reason stated at the top of this file: the display
 * maps used to be copied into every surface that drew a channel.
 *
 * A map and not a `channelIcon()` accessor like its neighbours: the react-compiler lint reads a
 * function that RETURNS a component as a component created during render. Callers index it, which
 * is total over the union — a raw wire value has to be narrowed with {@link isSellChannel} first,
 * and `ChannelBadge` renders nothing at all for one it cannot name.
 */
export const CHANNEL_ICONS: Record<SellChannel, LucideIcon> = {
  WHATSAPP: MessageSquare,
  WEB: Globe,
  EXTERNAL_AGENT: Bot,
};
